import { CodeBracketIcon } from "@heroicons/react/16/solid";
import { useStore } from "@nanostores/react";
import { ReactElement } from "react";
import { FixedSizeList as List } from "react-window";

import type { UIGraphElement, UIGraphNode } from "../types/graph.ts";

import { $selectedElements } from "../stores/graph/selection.ts";
import { $workflowID } from "../stores/graph/workflow-result.ts";
import { $workflowsByGraphID } from "../stores/workflows.ts";
import { Button } from "./catalyst/Button.tsx";
import { SidebarHeading } from "./catalyst/SidebarHeading.tsx";
import { GraphDetailField } from "./GraphDetailField.tsx";

const ROW_HEIGHT = 70;
const VISIBLE_ROWS = 2;
const LIST_HEIGHT = ROW_HEIGHT * VISIBLE_ROWS;

// Type guard to check if selected data is from a UIGraphNode (has workflowIds)
const isNodeData = (
  element: UIGraphElement["data"],
): element is UIGraphNode["data"] => {
  return (
    element &&
    "entityId" in element &&
    "value" in element &&
    !("sourceId" in element) &&
    !("nodeIds" in element)
  );
};

export const ActionPayloads = (): ReactElement => {
  const selectedElements = useStore($selectedElements);
  const firstSelectedElement = selectedElements[0];
  const { data } = useStore($workflowsByGraphID);

  // Only nodes with workflowsIds, NOT clusters
  const filteredWorkflows = data?.filter((workflow) => {
    if (!firstSelectedElement || !isNodeData(firstSelectedElement)) {
      return false;
    }
    return firstSelectedElement.workflowIds?.includes(workflow.id);
  });

  const handleResponseClick = (id: string): void => {
    $workflowID.set(id);
  };

  if (!filteredWorkflows || filteredWorkflows.length === 0) {
    return <div className="mt-auto max-h-48 min-h-48" />;
  }

  return (
    <div className="nice-scrollbar mt-auto max-h-48 min-h-48 flex-none overflow-y-auto border-t border-t-zinc-800 px-4 py-2">
      <SidebarHeading className="!mb-2 !p-0">Action Payloads</SidebarHeading>

      <List
        className="nice-scrollbar h-full overflow-y-auto"
        height={LIST_HEIGHT}
        itemCount={filteredWorkflows.length}
        itemSize={ROW_HEIGHT}
        width="100%"
      >
        {({ index, style }) => {
          const item = filteredWorkflows[index];

          if (!item) {
            return null;
          }

          return (
            <div style={style}>
              <GraphDetailField key={item.id} label={item.title}>
                <Button
                  onClick={() => handleResponseClick(item.id)}
                  outline
                  size="xs"
                >
                  <CodeBracketIcon className="fill-white" />
                  Response
                </Button>
              </GraphDetailField>
            </div>
          );
        }}
      </List>
    </div>
  );
};
